import { motion } from "framer-motion";
import { 
  Wand2, 
  Palette, 
  BookOpen, 
  Download,
  Sparkles,
  Camera
} from "lucide-react";

const features = [
  {
    icon: Camera,
    title: "Ladda upp ett foto",
    description: "AI analyserar bilden och skapar en unik karaktär baserad på ditt barns utseende.",
    color: "bg-secondary/20 text-teal-deep",
  },
  {
    icon: Wand2,
    title: "AI-genererad berättelse",
    description: "Avancerad AI skriver en engagerande och åldersanpassad historia med ditt barn i huvudrollen.",
    color: "bg-primary/10 text-primary",
  },
  {
    icon: Palette,
    title: "Unika illustrationer",
    description: "Varje sida får egna AI-genererade illustrationer som matchar berättelsen och din karaktär.",
    color: "bg-orange/10 text-orange-warm",
  },
  {
    icon: BookOpen,
    title: "Interaktiv läsupplevelse",
    description: "Läs boken direkt i appen med vacker layout och smidiga sidvändningar.",
    color: "bg-gold/15 text-gold-warm",
  },
  {
    icon: Download,
    title: "Ladda ner som PDF",
    description: "Spara boken som PDF för att skriva ut eller läsa offline när som helst.",
    color: "bg-secondary/20 text-teal-deep",
  },
  {
    icon: Sparkles,
    title: "Obegränsad kreativitet",
    description: "Välj mellan olika teman och stilar – från rymdäventyr till undervattensvärld.",
    color: "bg-primary/10 text-primary",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const FeaturesSection = () => {
  return (
    <section id="features" className="py-20 sm:py-28">
      <div className="container mx-auto">
        {/* Section header */}
        <motion.div 
          className="text-center max-w-2xl mx-auto mb-12 sm:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary/20 text-teal-deep text-sm font-medium mb-4">
            Funktioner
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-foreground mb-4">
            Allt du behöver för att skapa personliga böcker
          </h2>
          <p className="text-lg text-muted-foreground">
            Från foto till färdig bok på bara några minuter – AI gör det tunga lyftet.
          </p>
        </motion.div>

        {/* Features grid - Bento style */}
        <motion.div 
          className="bento-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="group p-6 sm:p-8 rounded-3xl bg-card shadow-paper border border-border/50 hover:shadow-paper-hover hover:-translate-y-1 transition-all duration-300 text-center sm:text-left flex flex-col items-center sm:items-start"
            >
              <div className={`w-12 h-12 rounded-2xl ${feature.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <feature.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-bold text-foreground mb-2">
                {feature.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
